import queryString from 'query-string'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import pick from 'lodash/pick'

import { buildUserAttributes } from '@/utils/launchDarkly'
import { fetchOrgHierarchy } from '@/reducers/orgHierarchy/orghierarchy.actions'
import { apiService, fetchingAPI } from '@/api'
import { clearStoredTheme } from '@/utils/hooks/useTheme'
import { clearAuthMessage, updateAuthMessage, openModal } from '../ui/ui.redux'
import {
  deriveRbacFlags,
  loginUser,
  logoutUser,
  PERMISSIONS_STATUS,
  RBAC_FLAGS,
  SESSION_ONLY_USER_FIELDS,
} from './currentUser.redux'
import { setPendingToken } from './mfa.redux'
import { clearSSOReturnPath, peekSSOReturnPath, safeNextPath, withNext } from './loginNext.helpers'

// Keeps the user signed in across a refresh, minus this session's permissions
// fetch: a reload must wait for a fresh one rather than trust a stored copy.
const storeUser = (user) =>
  localStorage.setItem('User', JSON.stringify(omit(user, SESSION_ONLY_USER_FIELDS)))

export const authenticateUser = (userData, setSubmitting) => {
  return async (dispatch) => {
    const { email, password, token, history, location, ldClient } = userData
    let flags = { showCommandCenter: true }
    let commandCenterEnabled = true

    dispatch(clearAuthMessage())

    try {
      const { next: requestedNext } = queryString.parse(location.search)
      // An SSO login comes back as /login?token=... with no next; its return path waits in
      // sessionStorage until a login succeeds.
      const next = isEmpty(requestedNext) && !isEmpty(token) ? peekSSOReturnPath() : requestedNext
      const response = await fetch(`${apiService.auth}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, token }),
      })

      if (response.status === 401) {
        const authErr = await response.text()
        dispatch(updateAuthMessage('error', authErr || 'Incorrect username or password.'))
        if (setSubmitting) return setSubmitting(false)
        return null
      }

      if (response.status >= 500) {
        dispatch(
          updateAuthMessage(
            'error',
            'The server encountered an internal error and was unable to login.'
          )
        )
        if (setSubmitting) return setSubmitting(false)
        return null
      }

      const currentUser = await response.json()

      if (currentUser.error) {
        dispatch(updateAuthMessage('error', 'Something went wrong.'))
        if (setSubmitting) return setSubmitting(false)
        return null
      }

      // if a pending_token is received, that means the organization enforces 2 factor authentication
      // and the user must complete the code challenge to get the actual token
      if (currentUser.pending_token) {
        // currentUser will have keys pending_token, mfa_type, organization_id, username
        dispatch(setPendingToken(currentUser))
        // Carry next through MFA so the user still lands where they were headed.
        return history.push(withNext('/login_mfa', next))
      }

      // The login succeeded, so a saved SSO return path has done its job.
      clearSSOReturnPath()

      // for hierarchy managers, the user's organizationid will change to whichever org they selected
      // we store own_organization_id as the user's true organization id
      currentUser.own_organization_id = currentUser.organizationid

      // Persist current user in localstorage so user remains logged in when they refresh the page
      storeUser(currentUser)

      // Log user into Redux
      dispatch(loginUser(currentUser))
      dispatch(clearAuthMessage())

      // Initialize LaunchDarkly
      if (ldClient) {
        await ldClient.identify(buildUserAttributes(currentUser))
        flags = ldClient.allFlags()
        commandCenterEnabled = flags?.showCommandCenter !== false
      }

      const defaultDestination =
        currentUser.organizationid === 1
          ? '/organizations'
          : commandCenterEnabled
            ? '/live-listen'
            : '/explorer'

      // This line prevents some back end errors that are blowing up the logs
      if (next === '/users/' && currentUser.organizationid === 1) {
        return history.push('/live-listen')
      }
      const redirect = safeNextPath(next) || defaultDestination

      return history.push(redirect)
    } catch (err) {
      console.error(err)

      dispatch(updateAuthMessage('error', 'Something went wrong, please try again.'))
      if (setSubmitting) return setSubmitting(false)
      return null
    }
  }
}

// Forgets the session saved in this browser. Doesn't touch Redux or navigate.
export const clearSavedSession = () => {
  localStorage.removeItem('User')
  localStorage.removeItem('balto_org_id')
  clearStoredTheme()
}

export const localLogoutUser = ({ history, location, ldClient }) => {
  return async (dispatch) => {
    const redirect = `/login${location && location.pathname !== '/login' ? `?next=${location.pathname}` : ''}`

    dispatch(logoutUser())

    clearSavedSession()

    if (ldClient) {
      ldClient.identify({ anonymous: true })
    }

    history.push(redirect)
  }
}

export const unauthenticateUser = ({ history, location, ldClient, callback }) => {
  return async (dispatch) => {
    try {
      await fetchingAPI(`${apiService.auth}/api/logout`, 'POST', dispatch)
    } catch (err) {
      console.error(err)
    }

    dispatch(localLogoutUser({ history, location, ldClient, callback }))
  }
}

export const resetUserPassword = (email, setSubmitting) => async (dispatch) => {
  dispatch(clearAuthMessage())

  try {
    const response = await fetch(`${apiService.auth}/api/reset_password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username: email }),
    })

    if (response.status !== 200) {
      dispatch(updateAuthMessage('error', 'Something went wrong, please try again.'))
      return setSubmitting(false)
    }

    const currentUser = await response.json()

    if (currentUser.error) {
      dispatch(updateAuthMessage('error', currentUser.error))
      return setSubmitting(false)
    }

    dispatch(
      updateAuthMessage(
        'success',
        "We sent an email with a link to reset your password. If you don't see the email from noreply@baltocloud.com, please check your spam folder."
      )
    )
    return setSubmitting(false)
  } catch (err) {
    dispatch(updateAuthMessage('error', 'Something went wrong, please try again.'))
    return setSubmitting(false)
  }
}

// Brings the authority flags on `currentUser` in line with what the backend
// applies (see deriveRbacFlags). Run whenever either input can change: the
// user's permissions arriving, and the org record loading or switching, since
// the rollout mode is read off the org. A no-op when nothing would change.
export const syncRbacFlags = () => (dispatch, getState) => {
  const { currentUser = {}, currentOrganization = {} } = getState()

  if (!currentUser.token) {
    return
  }

  const flags = deriveRbacFlags(currentUser, currentOrganization)
  const changed = Object.entries(flags).some(([flag, value]) => currentUser[flag] !== value)

  if (!changed) {
    return
  }

  const syncedUser = { ...currentUser, ...flags }
  dispatch(loginUser(syncedUser))
  storeUser(syncedUser)
}

// `refresh` is a re-read while the app is open (the tab came back into view):
// it updates the permissions only, without the once-per-load side effects.
export const fetchCurrentUserPermissions = (userId, organizationId, { refresh = false } = {}) => {
  return async (dispatch, getState) => {
    // A reply that lands after logout, or after another account signed in,
    // belongs to a session that is gone: writing it would recreate the cleared
    // user or overwrite the new one's permissions.
    const sessionToken = getState().currentUser?.token
    const sessionEnded = () => getState().currentUser?.token !== sessionToken

    try {
      const userPermissions = await fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/users/${userId}/permissions`,
        'GET',
        dispatch,
        undefined,
        undefined,
        undefined,
        true
      )

      if (sessionEnded()) {
        return
      }

      // An older core-service may answer without `resolved_permissions` (or with
      // nothing). Take the field from this response only — never a stored copy
      // from an earlier session — and still flip the status so a route guard
      // waiting on it can render.
      const { currentUser = {} } = getState()
      const updatedUser = {
        ...currentUser,
        ...userPermissions,
        // The raw flags as the server sent them; syncRbacFlags may overwrite
        // the live ones from the capabilities and needs these to put them back.
        legacy_flags: isEmpty(userPermissions)
          ? currentUser.legacy_flags
          : pick(userPermissions, RBAC_FLAGS),
        resolved_permissions: userPermissions?.resolved_permissions ?? null,
        home_rbac_mode: userPermissions?.home_rbac_mode ?? null,
        permissions_status: PERMISSIONS_STATUS.LOADED,
      }
      dispatch(loginUser(updatedUser))
      storeUser(updatedUser)
      dispatch(syncRbacFlags())

      if (refresh) {
        return
      }

      if (!isEmpty(userPermissions) && !userPermissions.metrics_job_function) {
        dispatch(openModal('metricsJobFunctionModal'))
      }

      // get org hierarchy (other orgs user has permission for) if user is hierarchy manager
      if (userPermissions?.hierarchy_manager) {
        dispatch(fetchOrgHierarchy())
      }
    } catch (err) {
      console.error(err)

      // A 401 rejects with the logout thunk's promise, which clears the session
      // only once the logout request returns; wait for it so the check below
      // sees the session ended instead of writing the user back.
      if (typeof err?.then === 'function') {
        await err.catch(() => {})
      }

      // Any other failure, including a 400/409/502 whose body rejects as a plain
      // object, must still set FAILED, or a route guard waits on it forever.
      if (sessionEnded()) {
        return
      }

      // Capabilities from a previous session may belong to a role the user no
      // longer has, so they are dropped. With none, the UI falls back to the
      // legacy flags — what it showed before RBAC — and a banner says the
      // permissions could not be loaded.
      const failedUser = {
        ...getState().currentUser,
        resolved_permissions: null,
        permissions_status: PERMISSIONS_STATUS.FAILED,
      }
      dispatch(loginUser(failedUser))
      storeUser(failedUser)
      dispatch(syncRbacFlags())
    }
  }
}
